import React, { useRef, useState } from "react";
import { useViewerDecks, useViewerQuery } from "queries/viewerQuery";
import styled from "styled-components/macro";
import { Stack } from "@mui/material";
import ChatBubble from "components/assistant/ChatBubble";
import { graphql } from "babel-plugin-relay/macro";
import { useMutation } from "relay-hooks";
import {
  AssistantMode,
  AssistantMutation,
  AssistantMutation$data,
  ChatMessageOwnerType
} from "components/assistant/__generated__/AssistantMutation.graphql";
import { AssistantAddChatMessagesMutation } from "components/assistant/__generated__/AssistantAddChatMessagesMutation.graphql";
import { useCloseAssistant, useRemainingAiMessages } from "components/assistant/assistantHooks";
import ChatSuggestions from "components/assistant/suggestions/ChatSuggestions";
import ChatIdeas, { IdeaTag } from "components/assistant/ChatIdeas";
import { useTranslates } from "hooks/useTranslates";
import { useInitialMessageText } from "components/assistant/useInitialMessageText";
import { useChatIdeas, useChatIdeasSettings } from "hooks/useChatIdeas";
import { useKeepBurning } from "../fire/xpHooks";
import { Optional } from "utility-types";
import AiMessagesLimitDialog from "./AiMessagesLimitDialog/AiMessagesLimitDialog";
import { FullChatWarning, getChatMaxMessages } from "./FullChat";
import { ListenVocabControls, useListenVocabSettings } from "./ListenVocabControls";
import { CardValues, ChatIdeasSettings, useGetState, useSetState } from "components/ReduxProvider";
import { useEvent, usePrevious, useLatest } from "react-use";
import { EVENT, snackbar } from "../../tools/events";
import { TranslatedSuggestion } from "./suggestions/suggestionsDefinitions";
import { useIsQuestListenVocab } from "../../root/main/main/quests/ListenVocabQuest";
import { useIsQuestAiAssistant } from "../../root/main/main/quests/AiAssistantQuest";
import { MAX_LISTEN_VOCAB_CARDS } from "sharedJs__generated/constants";
import { RemainingMessages } from "./RemainingMessages/RemainingMessages";
import AssistantMenu from "./AssistantMenu";
import { useDBLog } from "hooks/useDBLog";
import { SUBSCRIPTION } from "../../root/profile/ProfileRouter.config";
import { useNavigate } from "react-router-dom";
import { getTheme } from "../../styled/theme";
import { useStateWithRef } from "hooks/useStateWithRef";
import { createGrammarFeedbackContext } from "queries/grammarFeedback";
import { silenceTts } from "queries/tts/ttsTools";
import { Trans } from "react-i18next";
import { useSpeakingStarterText } from "root/main/main/quests/misc/useSpeakingStarters";
import AssistantInput from "./AssistantInput";
import { bottomPadding } from "tools/device";

export type TChatBubble = Optional<
  AssistantMutation$data["askAssistant"]["chat"]["chatMessages"][0],
  "analysis" | "translation" | "id"
>;
export type TChatBubbles = TChatBubble[];

const LISTEN_VOCAB_MESSAGE_BUFFER = 2; // How many messages to show ahead

const ChatAreaWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  overflow: hidden;
`;
const ChatArea = styled.div`
  padding: 20px;
  background-color: ${(p) => p.theme.duo.color.lightGrey};
  height: 100%;
  overflow: auto;
`;
const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 20px;
  background: linear-gradient(to bottom, transparent, ${(p) => p.theme.duo.color.lightGrey});
  pointer-events: none;
  z-index: 1;
`;

const InputRow = styled.div<{ $backgroundColor?: string }>`
  padding: 16px;
  padding-left: 24px;
  border-top: 1px solid ${(p) => p.theme.duo.color.lightGrey};
  background-color: ${(p) => p.$backgroundColor || p.theme.duo.palette.white};
  border-radius: 0 0 12px 12px;
  // ios fix white bottom line
  margin-bottom: -${bottomPadding}px;
  padding-bottom: ${16 + bottomPadding}px;
`;
// Header elements moved to AssistantMenu.tsx

const askAssistant = graphql`
  mutation AssistantMutation(
    $chatMessage: ChatMessageInputType!
    $deckId: String!
    $chatId: String
    $mode: AssistantMode!
    $promptVariables: [GenericVariableInput!]
    $initialMessage: String
  ) {
    askAssistant(
      chatMessage: $chatMessage
      deckId: $deckId
      chatId: $chatId
      mode: $mode
      promptVariables: $promptVariables
      initialMessage: $initialMessage
    ) {
      chat {
        id
        chatMessages {
          id
          text
          translation
          analysis
          owner
        }
      }
      viewer {
        aiMessagesUsedThisWeek
        flags {
          name
          value
          body
          updated
        }
      }
    }
  }
`;

const addChatMessagesGQL = graphql`
  mutation AssistantAddChatMessagesMutation(
    $chatMessages: [ChatMessageInputType!]!
    $deckId: String!
    $chatId: String
    $mode: AssistantMode!
  ) {
    addChatMessages(chatMessages: $chatMessages, deckId: $deckId, chatId: $chatId, mode: $mode) {
      chat {
        id
        chatMessages {
          id
          text
          translation
          analysis
          owner
        }
      }
      viewer {
        id
      }
    }
  }
`;

type Props = {
  autoApplyMode?: AssistantMode;
  cardContext?: CardValues;
  onClose: () => void;
};

const Assistant: React.FC<Props> = ({ onClose, cardContext, autoApplyMode }) => {
  const dblog = useDBLog();
  const navigate = useNavigate();
  const { isSubscriber } = useViewerQuery();
  const { deck } = useViewerDecks();
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const lastBubbleRef = React.useRef<HTMLDivElement>(null);
  const chatIdRef = React.useRef<string | undefined>(undefined);
  const [chatResetKey, setChatResetKey] = React.useState(0);
  const [chatBubbles, setChatBubbles, chatBubblesRef] = useStateWithRef<TChatBubbles>([]);
  const [sending, setSending] = React.useState(false);
  const { translates } = useTranslates(
    deck?.front || "",
    ["✅ correct answer", "❌ wrong, right answer is", "next question", "Make a story from my cards"],
    !deck
  );
  const [correctText, wrongAnswerText, nextQuestionText, aiStoryInitialMessage] = translates;
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  const modeRef = React.useRef<AssistantMode>(autoApplyMode || "assistant");
  const keepBurning = useKeepBurning();
  const deckFrontPrev: string | undefined = usePrevious(deck?.front);
  const closeAssistant = useCloseAssistant();
  const [isListenVocabPlaying, setIsListenVocabPlaying, isListenVocabPlayingRef] = useStateWithRef(false);
  const [listenVocabSettings] = useListenVocabSettings();
  const oldChatIdRef = React.useRef<string | undefined>(undefined);
  const [visibleBubbleCount, setVisibleBubbleCount, visibleBubbleCountRef] = useStateWithRef(0);
  const userMessagesCount = chatBubbles.slice(0, visibleBubbleCount).filter((bubble) => bubble.owner === "user").length;
  const assistantMessagesCount = chatBubbles
    .slice(0, visibleBubbleCount)
    .filter((bubble) => bubble.owner === "assistant").length;

  const visibleBubbles = chatBubbles
    .slice(0, visibleBubbleCount)
    .filter((chatBubble) => modeRef.current !== "listenVocab" || chatBubble.owner === "assistant");
  const { getInitialMessageText, loading: isInitialMessageLoading } = useInitialMessageText({
    cardContext
  });
  const [initialMessageText, setInitialMessageText] = useState(getInitialMessageText(modeRef.current));
  const { updateStarter: updateSpeakingStarter } = useSpeakingStarterText({
    skipTranslates: modeRef.current !== "speaking"
  });

  const handleListenVocabResponse = (messages: TChatBubbles) => {
    const isInitialMessage = messages.length === 2;
    const shouldRequest = // prefetch when first message or when only one message left
      isInitialMessage ||
      (messages.length === visibleBubbleCountRef.current - LISTEN_VOCAB_MESSAGE_BUFFER &&
        messages.length < getChatMaxMessages(modeRef.current) * 2);
    if (!shouldRequest) return;
    if (isInitialMessage) setVisibleBubbleCount(messages.length);
    if (remainingAiMessages > 0) {
      requestAssistant(nextQuestionText, { prefetch: true });
    }
  };

  const handleListenVocabTTSEnded = (chatBubble: TChatBubble) => {
    if (!isListenVocabPlayingRef.current) return;
    if (modeRef.current !== "listenVocab") return;

    const bubbleIndex = chatBubblesRef.current.findIndex((bubble) => bubble.id === chatBubble.id);
    if (bubbleIndex === -1) return;

    const bubblesLength = chatBubblesRef.current.length;
    const reachedMaxCards = bubblesLength === MAX_LISTEN_VOCAB_CARDS * 2 && bubbleIndex === bubblesLength - 1;
    const visibleCount = visibleBubbleCountRef.current;
    const isLastBubble = bubbleIndex + 1 === visibleCount;

    if (reachedMaxCards || (remainingAiMessages === 0 && isLastBubble)) {
      setIsListenVocabPlaying(false);
      return;
    }

    const needPrefetch =
      isLastBubble &&
      visibleCount >= bubblesLength - LISTEN_VOCAB_MESSAGE_BUFFER &&
      bubblesLength < getChatMaxMessages(modeRef.current) * 2;

    if (needPrefetch) {
      requestAssistant("Next", { prefetch: true });
    }

    if (isLastBubble) {
      setVisibleBubbleCount(bubbleIndex + 1 + LISTEN_VOCAB_MESSAGE_BUFFER);
      scrollToBottom();
    }
  };

  const [mutate] = useMutation<AssistantMutation>(askAssistant, {
    onCompleted: (data) => {
      if (oldChatIdRef.current === data.askAssistant.chat.id && oldChatIdRef.current !== undefined) return; // ignore old completions
      chatIdRef.current = data.askAssistant.chat.id;

      setChatBubbles([...data.askAssistant.chat.chatMessages]);
      setSending(false);

      if (modeRef.current === "listenVocab") {
        handleListenVocabResponse([...data.askAssistant.chat.chatMessages]);
      } else {
        setVisibleBubbleCount(data.askAssistant.chat.chatMessages.length);
      }

      if (modeRef.current === "speaking" && data.askAssistant.chat.chatMessages.length === 3) {
        updateSpeakingStarter();
      }

      if (chatIdeasSettingsRef.current.active) {
        generateIdeas({
          chatId: data.askAssistant.chat.id,
          mode: modeRef.current,
          withTranslation: chatIdeasSettingsRef.current.withTranslation
        });
      }
    }
  });

  const setQuestAiAssistant = useSetState("questAiAssistant");
  const setQuestListenVocab = useSetState("questListenVocab");

  const remainingAiMessages = useRemainingAiMessages();
  const assistantAutoSpeechOff = useGetState("assistantAutoSpeechOff");

  const { ideas: chatIdeas, loading: chatIdeasLoading, generateIdeas } = useChatIdeas(deck?.id || "");
  const [chatIdeasSettings] = useChatIdeasSettings({ mode: modeRef.current });
  const listenVocabSettingsRef = useLatest(listenVocabSettings);
  const chatIdeasSettingsRef = useRef(chatIdeasSettings);
  const isQuestListenVocab = useIsQuestListenVocab();
  const isQuestListenVocabRef = useLatest(isQuestListenVocab);
  const isAssistantQuest = useIsQuestAiAssistant();
  const isAssistantQuestRef = useLatest(isAssistantQuest);

  React.useEffect(() => {
    chatIdeasSettingsRef.current = chatIdeasSettings;
  }, [chatIdeasSettings]);

  const clearChat = React.useCallback(() => {
    setChatResetKey((prev) => prev + 1);
    setChatBubbles([]);
    oldChatIdRef.current = chatIdRef.current;
    chatIdRef.current = undefined;
    setSending(false);
    setInitialMessageText(getInitialMessageText(modeRef.current));
    setVisibleBubbleCount(0);
  }, [setChatBubbles, setSending, setVisibleBubbleCount, getInitialMessageText, setInitialMessageText]);

  React.useEffect(() => {
    // only clear chat if user manually changes deck
    if (deck?.front !== deckFrontPrev && deckFrontPrev) {
      clearChat();
    }
  }, [deck?.front, deckFrontPrev, clearChat]);

  const shouldShowIdeasInitially = () => !["assistant", "cardDetail"].includes(modeRef.current);

  // generate ideas when new chat is created
  React.useEffect(() => {
    if (isInitialMessageLoading) return;
    if (
      chatBubbles.length === 0 &&
      initialMessageText &&
      chatIdeasSettingsRef.current.active &&
      shouldShowIdeasInitially()
    ) {
      generateIdeas({
        question: initialMessageText,
        mode: modeRef.current,
        withTranslation: chatIdeasSettingsRef.current.withTranslation
      });
    }
  }, [chatBubbles.length, generateIdeas, isInitialMessageLoading, initialMessageText]);

  useEvent(EVENT.resetAssistant, () => {
    clearChat();
  });

  useEvent(EVENT.regenerateChatIdeas, (e: CustomEvent<ChatIdeasSettings>) => {
    if (!e.detail.active) return;
    generateIdeas({
      mode: modeRef.current,
      withTranslation: e.detail.withTranslation,
      chatId: chatIdRef.current,
      question: chatBubbles.length === 0 ? initialMessageText : undefined
    });
  });

  const scrollToBottom = React.useCallback(() => {
    setTimeout(() => {
      lastBubbleRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  const handleSuggestionClick = (suggestion: TranslatedSuggestion) => {
    if (suggestion.switchToMode === "listenVocab" && deck?.stats.total === 0) {
      snackbar(<Trans>No cards to listen to. Add some cards to your deck to get started.</Trans>, {
        severity: "warning"
      });
      return;
    }

    if (suggestion.switchToMode) {
      modeRef.current = suggestion.switchToMode;
    }

    if (suggestion.behavior === "goesToInput") {
      if (!inputRef.current) return;
      inputRef.current.value = suggestion.targetText + " ";
      inputRef.current.focus();
    }

    if (suggestion.behavior === "sendsMessage") {
      requestAssistant(suggestion.targetText);
    }

    if (suggestion.behavior === "closesAssistant") {
      const chatMessage: TChatBubble = {
        id: "",
        text: suggestion.targetText,
        owner: "user" as ChatMessageOwnerType
      };
      setChatBubbles([...chatBubbles, chatMessage]);
      scrollToBottom();
      setTimeout(closeAssistant, 300);
    }
  };

  const ideaClickedTextsRef = React.useRef<Set<string>>(new Set());
  const handleIdeaClick = async (idea: string, tags?: string[]) => {
    ideaClickedTextsRef.current.add(idea);
    if (modeRef.current === "letsPractise") {
      const isCorrect = tags?.includes(IdeaTag.CORRECT);
      const correctIdea = chatIdeas.find((i) => i.tags?.includes(IdeaTag.CORRECT))?.idea;
      const feedbackText = isCorrect ? correctText : wrongAnswerText + " " + correctIdea;
      await addMessages([{ text: idea, owner: "user" as ChatMessageOwnerType }]);
      setSending(true);
      await addMessages([{ text: feedbackText, owner: "assistant" as ChatMessageOwnerType }]);
      requestAssistant(nextQuestionText, { skipLocalUserAppend: true });
    } else {
      requestAssistant(idea);
    }
  };

  const handleSend = () => {
    const value = inputRef.current?.value;
    if (!value || !value.trim()) return;
    requestAssistant(value);
    inputRef.current.value = "";
  };

  const requestAssistant = async (
    message: string,
    options: { skipLocalUserAppend?: boolean; prefetch?: boolean } = {}
  ) => {
    if (!deck) return;
    if (deck.stats.total === 0 && modeRef.current === "listenVocab") return;

    const chatMessage: TChatBubble = {
      id: "",
      text: message,
      owner: "user" as ChatMessageOwnerType
    };

    const promptVariables: { key: string; value: string }[] = [];
    if (cardContext) promptVariables.push({ key: "cardContextFront", value: cardContext.front });
    if (modeRef.current === "listenVocab") {
      promptVariables.push({
        key: "withTranslation",
        value: listenVocabSettingsRef.current.withTranslation.toString()
      });
    }

    let initialMessage: string | undefined;
    if (chatBubblesRef.current.length === 0) {
      initialMessage = getInitialMessageText(modeRef.current);
      setInitialMessageText(initialMessage);
    }

    mutate({
      variables: {
        chatMessage,
        deckId: deck.id,
        chatId: chatIdRef.current,
        mode: modeRef.current,
        promptVariables,
        initialMessage: initialMessage || undefined
      }
    });

    if (!options.prefetch) {
      setIsListenVocabPlaying(modeRef.current === "listenVocab");
      setSending(true);
      if (!options.skipLocalUserAppend) {
        setVisibleBubbleCount(chatBubblesRef.current.length + 1);
        setChatBubbles([...chatBubblesRef.current, chatMessage]);
      }
      keepBurning(15);
    }
    scrollToBottom();

    if (modeRef.current === "assistant" && isAssistantQuestRef.current) {
      setQuestAiAssistant(true);
    }

    if (modeRef.current === "listenVocab" && isQuestListenVocabRef.current) {
      setQuestListenVocab(true);
    }
  };

  React.useEffect(() => {
    if (!aiStoryInitialMessage || !nextQuestionText || !autoApplyMode) return;
    switch (autoApplyMode) {
      case "aiStory":
        requestAssistant(aiStoryInitialMessage);
        break;
      case "listenVocab":
      case "letsPractise":
        requestAssistant(nextQuestionText, { skipLocalUserAppend: true });
        break;
    }
  }, [autoApplyMode, nextQuestionText, aiStoryInitialMessage, chatResetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const [addChatMessages] = useMutation<AssistantAddChatMessagesMutation>(addChatMessagesGQL);

  const addMessages = React.useCallback(
    async (messages: TChatBubbles) => {
      if (!deck) return;
      return new Promise<void>((resolve) => {
        addChatMessages({
          variables: {
            chatMessages: messages.map((m) => ({ text: m.text || "", owner: m.owner! })),
            deckId: deck.id,
            chatId: chatIdRef.current,
            mode: modeRef.current
          },
          onCompleted: (data) => {
            const { chat } = data.addChatMessages;
            chatIdRef.current = chat.id;
            setChatBubbles([...chat.chatMessages]);
            setVisibleBubbleCount(chat.chatMessages.length);
            resolve();
          }
        });
      });
    },
    [addChatMessages, deck, setChatBubbles, setVisibleBubbleCount]
  );

  const handleTranscription = (text: string) => {
    if (text.length > 0) requestAssistant(text);
  };

  React.useEffect(() => {
    chatIdeasLoading && scrollToBottom();
  }, [chatIdeasLoading, scrollToBottom]);

  React.useEffect(() => {
    if (!isInitialMessageLoading) {
      setInitialMessageText(getInitialMessageText(modeRef.current));
    }
  }, [getInitialMessageText, isInitialMessageLoading, setInitialMessageText]);

  if (!deck) return null;

  const isPlayOnMountEnabledForBubble = (chatBubble: TChatBubble) => {
    if (modeRef.current === "listenVocab") return true;
    if (chatBubblesRef.current.indexOf(chatBubble) === 0 && !["letsPractise", "speaking"].includes(modeRef.current)) {
      return false;
    }
    if (
      modeRef.current === "letsPractise" &&
      (chatBubble.text.startsWith(wrongAnswerText) || chatBubble.text.startsWith(correctText))
    ) {
      return false;
    }

    return !assistantAutoSpeechOff && isSubscriber;
  };

  return (
    <>
      {isMoreInfoDialogOpen && (
        <AiMessagesLimitDialog
          onShowSubscriptionOptions={() => {
            dblog("AI messages - more info", "user clicked on subscribe button");
            navigate(SUBSCRIPTION.url());
          }}
          onClose={() => setIsMoreInfoDialogOpen(false)}
        />
      )}

      <Stack direction="column" sx={{ flexWrap: "nowrap", height: "100%" }}>
        <AssistantMenu mode={modeRef.current} onBack={onClose} />

        <ChatAreaWrapper>
          <ChatArea>
            <Stack direction={"column"} spacing={2.5}>
              {!isInitialMessageLoading && initialMessageText && assistantMessagesCount === 0 && (
                <ChatBubble
                  chatMessage={{
                    owner: "assistant",
                    text: initialMessageText
                  }}
                  onBurn={() => keepBurning(9)}
                  onTTSClick={() => {
                    setIsListenVocabPlaying(false);
                  }}
                />
              )}
              {visibleBubbles.map((chatBubble, i) => (
                <ChatBubble
                  key={i}
                  chatMessage={chatBubble}
                  onBurn={() => keepBurning(9)}
                  ref={i === visibleBubbles.length - 1 ? lastBubbleRef : null}
                  playOnMount={isPlayOnMountEnabledForBubble(chatBubble)}
                  assistantMode={modeRef.current}
                  grammarFeedbackContext={createGrammarFeedbackContext({
                    prevMessageText: i > 0 ? visibleBubbles[i - 1]?.text : initialMessageText || "",
                    assistantMode: modeRef.current
                  })}
                  defaultGrammarFeedbackEnabled={
                    modeRef.current !== "listenVocab" && !ideaClickedTextsRef.current.has(chatBubble.text || "")
                  }
                  additionalInfo={
                    i === visibleBubbles.length - 1 &&
                    !isSubscriber &&
                    chatBubble.owner === "assistant" && (
                      <RemainingMessages
                        remainingMessages={remainingAiMessages}
                        onShowMoreInfo={() => {
                          dblog("AI messages - more info", "dialog opened");
                          setIsMoreInfoDialogOpen(true);
                        }}
                      />
                    )
                  }
                  onTTSEnded={() => {
                    handleListenVocabTTSEnded(chatBubble);
                  }}
                  onTTSClick={() => {
                    setIsListenVocabPlaying(false);
                  }}
                />
              ))}
              {(sending || (isInitialMessageLoading && assistantMessagesCount === 0)) && (
                <ChatBubble chatMessage={{ owner: "assistant", text: "" }} isTyping />
              )}
              {!sending &&
                ((visibleBubbles.length === 0 && shouldShowIdeasInitially()) ||
                  (visibleBubbles.length > 0 && visibleBubbles[visibleBubbles.length - 1].owner === "assistant")) && (
                  <ChatIdeas
                    mode={modeRef.current}
                    ideas={chatIdeas}
                    onIdeaClick={handleIdeaClick}
                    loading={chatIdeasLoading}
                  ></ChatIdeas>
                )}
            </Stack>
          </ChatArea>
          <BottomGradient />
        </ChatAreaWrapper>

        {remainingAiMessages > 0 && !isInitialMessageLoading && (
          <ChatSuggestions
            chatMode={modeRef.current}
            messagesCount={visibleBubbles.length}
            onUseSuggestion={handleSuggestionClick}
          />
        )}
        <InputRow
          $backgroundColor={
            ["speaking", "listenVocab", "letsPractise"].includes(modeRef.current)
              ? getTheme().color.lightGrey
              : undefined
          }
        >
          <FullChatWarning messagesCount={userMessagesCount} mode={modeRef.current} />
          {modeRef.current === "listenVocab" &&
            visibleBubbles.length < getChatMaxMessages(modeRef.current) &&
            (remainingAiMessages > 0 || isListenVocabPlaying) && (
              <ListenVocabControls
                onPlay={() => {
                  setIsListenVocabPlaying(true);
                  handleListenVocabTTSEnded(visibleBubbles[visibleBubbles.length - 1]);
                }}
                onStop={() => {
                  setIsListenVocabPlaying(false);
                  silenceTts();
                }}
                playing={isListenVocabPlaying}
              />
            )}

          <AssistantInput
            inputRef={inputRef}
            disabled={Boolean(sending || isInitialMessageLoading || !remainingAiMessages)}
            onSend={handleSend}
            onTranscription={handleTranscription}
            mode={modeRef.current}
            userMessagesCount={userMessagesCount}
          />
        </InputRow>
      </Stack>
    </>
  );
};

export default Assistant;
